/** The `.seg` control: a row of options with the chosen one ringed.
 *
 * Two forms, because the design uses the same shape for two different jobs.
 * `Segmented` filters what is on screen — All / Published / Pending / Held,
 * or which driver a lap is compared against — so it is a group of buttons
 * that change state in place. `SegmentedNav` moves between screens — Account
 * and Tokens, the five admin sections — so it is a group of links, and the
 * active one carries `aria-current="page"` rather than a pressed state. A
 * link that looks like a button should still be a link: it belongs in the
 * browser's history and it should open in a new tab when asked.
 */

import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';

export interface SegmentedOption<T> {
  value: T;
  label: ReactNode;
  /** A word after the label, usually a count in a tag. */
  badge?: ReactNode;
  disabled?: boolean;
  /** Fuller wording for assistive technology when the label is a number. */
  title?: string;
}

export interface SegmentedProps<T> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Names the group — "filter by status", "compare with". */
  label: string;
  className?: string;
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
  className,
}: SegmentedProps<T>) {
  return (
    <div className={['seg', className ?? ''].filter(Boolean).join(' ')} role="group" aria-label={label}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            className="seg-opt"
            aria-pressed={selected}
            title={option.title}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.badge}
          </button>
        );
      })}
    </div>
  );
}

export interface SegmentedNavItem {
  to: string;
  label: ReactNode;
  badge?: ReactNode;
  /** Matches deeper paths too — `/admin/issues/iss_…` still lights Issues. */
  end?: boolean;
}

export interface SegmentedNavProps {
  items: readonly SegmentedNavItem[];
  label: string;
  className?: string;
}

export function SegmentedNav({ items, label, className }: SegmentedNavProps) {
  return (
    <nav className={['seg', className ?? ''].filter(Boolean).join(' ')} aria-label={label}>
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end ?? false} className="seg-opt">
          {item.label}
          {item.badge}
        </NavLink>
      ))}
    </nav>
  );
}

export default Segmented;
